// ─────────────────────────────────────────────────────────────
// Measurement Protocol — TAB 1: TEST INFO
//
// Moved verbatim out of the original monolithic MPCreationIndex.js
// into its own file (per-tab page) so the MP module can be organized
// as: index (list) page, creation page, and one file per tab. No UI
// or behavior changes were made here.
//
// Wrapped in forwardRef so the parent (MPExpandedRow) can, on a global
// Submit, ask this tab "are your required fields filled in?" and, if
// not, jump here and focus the offending field — without this tab
// needing to know anything about the other tabs.
// ─────────────────────────────────────────────────────────────
import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useDispatch } from "react-redux";

import {
  toArray,
  iCls,
  iconInputCls,
  FLbl,
  IconField,
  Section,
  AutoComplete,
  MultiSelect,
  EmailPreviewModal,
  Toast,
  FormActions,
} from "../shared/mpShared";
import {
  getCompanyID,
  getEmployeeID,
  hasPrivilege,
  isSmileIntegrated,
  PRIVILEGES,
  Textarea,
} from "../../../Common/UIComponents";
import {
  MP_GetTestInfoDetails,
  MP_SaveTestInfo,
} from "../../../API_Service/MeasurementProtocol_Slice";

// ─────────────────────────────────────────────────────────────
// TAB 1 — TEST INFO
// Wrapped in forwardRef so the parent (MPExpandedRow) can, on a global
// Submit, ask this tab: "are your required fields filled in?" and, if
// not, jump here and focus the offending field — without this tab
// needing to know anything about the other tabs.
// ─────────────────────────────────────────────────────────────
const TestInfoTab = forwardRef(function TestInfoTab(
  { mpID, entry, mp, onSaved, onSubmitAll },
  ref,
) {
  const dispatch = useDispatch();

  // Initial state only seeds from `entry`/`mp` where those fields are
  // plain scalars (dates, numbers, free text). Anything that needs to be
  // matched against option lists (test names, DUT refs, harness, power
  // supply) is left empty here and populated by GetTestInfoDetails()
  // below, once the option lists have actually loaded from the API —
  // that avoids matching against stale/placeholder data.
  const [form, setForm] = useState({
    testNameIds: [],
    startDate: entry?.StartDate ?? "",
    endDate: entry?.EndDate ?? "",
    dutRefIds: [],
    harnessId: entry?.HarnessRefID
      ? { value: entry.HarnessRefID, label: entry.HarnessRefID }
      : null,
    powerLineMm: entry?.PowerLineLength ?? "",
    harnessLenMm: entry?.HarnessLength ?? "",
    battV: entry?.BatteryVoltage ?? "",
    powerSupply: null,
    dutCurrentA: entry?.DUTCurrent ?? "",
    opMode: entry?.OperatingMode ?? "",
  });
  const [errs, setErrs] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: "", type: "success" });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testNameOptions, setTestNameOptions] = useState([]);
  const [dutRefOptions, setDutRefOptions] = useState([]);
  const [harnessRefOptions, setHarnessRefOptions] = useState([]);
  const [powerSupplyOptions, setPowerSupplyOptions] = useState([]);

  const fieldRefs = useRef({});

  const notify = (msg, type = "success") => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 2500);
  };

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrs((e) => ({ ...e, [k]: undefined }));
  };

  // Mandatory fields: Start Date and End Date are always required.
  // Test Name is required ONLY when this app is running embedded inside
  // Smile (isSmileIntegrated()) — same convention already used for the
  // Calibration/Verification Due Date column on the Instrument table —
  // and is left optional when running standalone. Returns the error map
  // so both the Save button and the cross-tab Submit flow can reuse the
  // same rule set.
  const validateFields = () => {
    const e = {};
    if (!form.startDate) e.startDate = "Start Date is Required";
    if (!form.endDate) e.endDate = "End Date is Required";
    if (isSmileIntegrated()) {
      if (!form.testNameIds || form.testNameIds.length === 0)
        e.testNameIds = "Test Name is Required";
      if (!form.dutRefIds || form.dutRefIds.length === 0)
        e.dutRefIds = "DUT Ref No is Required";
      if (!form.harnessId) e.harnessId = "Harness Ref No is Required";
      if (!form.powerLineMm) e.powerLineMm = "Power Line Length is Required";
      if (!form.harnessLenMm) e.harnessLenMm = "Harness Length is Required";
      if (!form.battV) e.battV = "Battery Voltage is Required";
      if (!form.powerSupply) e.powerSupply = "Power Supply is Required";
      if (!form.dutCurrentA) e.dutCurrentA = "DUT Current is Required";
      if (!form.opMode) e.opMode = "Operating Mode is Required";
    }
    return e;
  };

  useEffect(() => {
    GetTestInfoDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpID]);

  const GetTestInfoDetails = async () => {
    try {
      const payload = { mpID: mpID, companyID: getCompanyID() };
      const res = await dispatch(MP_GetTestInfoDetails(payload));
      if (res?.payload?.data) {
        const { TestList, TestInfo, HarnessList, DutList, PowerSupplyList } =
          res.payload.data;
        const data = toArray(TestInfo)[0];
        setForm({
          testNameIds: data?.Project_Test_Det_Ids
            ? String(data.Project_Test_Det_Ids)
                .split(",")
                .filter(Boolean)
                .map((id) => {
                  const match = TestList?.find(
                    (t) => String(t.Project_Test_Det_Id) === String(id),
                  );
                  return {
                    value: match?.Project_Test_Det_Id,
                    label: match?.Test_Name,
                  };
                })
            : [],
          startDate: data?.StartDate?.slice(0, 10) ?? "",
          endDate: data?.EndDate?.slice(0, 10) ?? "",
          dutRefIds: data?.DUT_Ref_No
            ? data.DUT_Ref_No.split(",").map((d) => ({
                value: d,
                label: DutList?.find((r) => r.Lab_RefNo === d)?.label || d,
              }))
            : [],
          harnessId: data?.Harness_Ref_No
            ? { value: data.Harness_Ref_No, label: data.Harness_Ref_No }
            : null,
          powerLineMm: data?.Power_Length ?? "",
          harnessLenMm: data?.Harness_Length ?? "",
          battV: data?.Battery_Voltage ?? "",
          powerSupply: data?.Power_Supply
            ? { value: data.Power_Supply, label: data.Power_Supply }
            : null,
          dutCurrentA: data?.DUT_Current ?? "",
          opMode: data?.Operating_Mode ?? "",
        });
        setTestNameOptions(
          toArray(TestList).map((t) => ({
            value: t?.Project_Test_Det_Id,
            label: t?.Test_Name,
          })),
        );
        setDutRefOptions(
          toArray(DutList).map((d) => ({
            value: d.Lab_RefNo,
            label: d.Lab_RefNo,
          })),
        );
        setHarnessRefOptions(
          toArray(HarnessList).map((d) => ({
            value: d.Lab_RefNo,
            label: d.Lab_RefNo,
          })),
        );
        setPowerSupplyOptions(
          toArray(PowerSupplyList).map((p) => ({
            value: p.Power_Supply_Id,
            label: p.Power_Supply,
          })),
        );
      }
    } catch (err) {
      console.error("Error fetching test info details:", err);
      notify("Couldn't load test info. Please refresh and try again.", "error");
    }
  };

  const getPreviewRows = () => [
    {
      label: "Test Name",
      value: form.testNameIds?.map((t) => t.label).join(", "),
    },
    { label: "Start Date", value: form.startDate },
    { label: "End Date", value: form.endDate },
    {
      label: "DUT Ref No",
      value: form.dutRefIds?.map((o) => o.label).join(", "),
    },
    { label: "Harness Ref No", value: form.harnessId?.label },
    {
      label: "Power Line Length",
      value: form.powerLineMm && `${form.powerLineMm} mm`,
    },
    {
      label: "Harness Length",
      value: form.harnessLenMm && `${form.harnessLenMm} mm`,
    },
    { label: "Battery Voltage", value: form.battV && `${form.battV} V` },
    { label: "Power Supply", value: form.powerSupply?.label },
    {
      label: "DUT Current",
      value: form.dutCurrentA && `${form.dutCurrentA} A`,
    },
    { label: "Operating Mode", value: form.opMode },
  ];

  // Returns a promise so the cross-tab Submit flow can `await` each tab's
  // save in sequence and stop immediately if one of them fails.
  const handleSave = () =>
    new Promise((resolve, reject) => {
      const e = validateFields();
      if (Object.keys(e).length) {
        setErrs(e);
        notify("Please fill in the required fields.", "error");
        return;
      }
      setSaving(true);

      const payload = {
        MPId: mpID,
        ProjectTestDetId: form.testNameIds?.map((t) => t.value).join(","),
        StartDate: form.startDate,
        EndDate: form.endDate,
        DUTRefNumber: form.dutRefIds?.map((d) => d.value).join(","),
        PowerLineLength: form.powerLineMm,
        HarnessLength: form.harnessLenMm,
        BatteryVoltage: form.battV,
        PowerSupply: form.powerSupply?.value ?? "",
        DUTCurrent: form.dutCurrentA,
        OperatingMode: form.opMode,
        HarnessRefNo: form.harnessId?.value ?? "",
        SavedBy: getEmployeeID(),
        CompanyId: getCompanyID(),
      };
      dispatch(MP_SaveTestInfo(payload))
        .then((res) => {
          if (res?.payload?.data > 0) {
            setSaving(false);
            notify("Test info saved!");
            setTimeout(() => {
              onSaved?.();
              resolve(res);
            }, 1200);
            GetTestInfoDetails();
          } else {
            setSaving(false);
            notify("Couldn't save test info. Please try again.", "error");
            reject(new Error("Save returned no rows affected"));
          }
        })
        .catch((err) => {
          console.error("Error saving test info:", err);
          setSaving(false);
          notify("An error occurred while saving test info.", "error");
          reject(err);
        });
    });

  // Imperative API used by MPExpandedRow's cross-tab Submit flow.
  const validate = () => {
    const e = validateFields();
    setErrs(e);
    const firstKey = Object.keys(e)[0];
    return { valid: Object.keys(e).length === 0, field: firstKey };
  };

  const focusField = (field) => {
    setTimeout(() => {
      const node = fieldRefs.current[field];
      if (node) {
        node.focus?.();
        node.scrollIntoView?.({ behavior: "smooth", block: "center" });
      }
    }, 60);
  };

  useImperativeHandle(ref, () => ({
    validate,
    focusField,
    getPreviewRows,
    save: handleSave,
  }));

  return (
    <>
      <Toast msg={toast.msg} show={toast.show} type={toast.type} />
      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        mp={mp}
        subject={`Test Info Update — ${mp?.MPNo ?? "New MP"}`}
        rows={getPreviewRows()}
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start min-w-0">
        <div className="lg:col-span-2 space-y-4 min-w-0">
          <Section icon="event" title="Schedule">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FLbl required>Start Date</FLbl>
                <IconField icon="calendar_today">
                  <input
                    ref={(el) => (fieldRefs.current.startDate = el)}
                    type="date"
                    className={`${iconInputCls} ${errs.startDate ? "border-2 border-red-500" : "border border-slate-200"}`}
                    value={form.startDate}
                    onChange={(e) => set("startDate", e.target.value)}
                  />
                </IconField>
                {errs.startDate && (
                  <p className="text-xs text-red-500 mt-1">{errs.startDate}</p>
                )}
              </div>
              <div>
                <FLbl required>End Date</FLbl>
                <IconField icon="event_available">
                  <input
                    ref={(el) => (fieldRefs.current.endDate = el)}
                    type="date"
                    className={`${iconInputCls} ${errs.endDate ? "border-2 border-red-500" : "border border-slate-200"}`}
                    value={form.endDate}
                    min={form.startDate}
                    onChange={(e) => set("endDate", e.target.value)}
                  />
                </IconField>
                {errs.endDate && (
                  <p className="text-xs text-red-500 mt-1">{errs.endDate}</p>
                )}
              </div>
            </div>
          </Section>

          <Section icon="cable" title="Harness & Power Setup">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <FLbl required={isSmileIntegrated()}>Harness Ref No</FLbl>
                <div ref={(el) => (fieldRefs.current.harnessId = el)}>
                  <AutoComplete
                    options={harnessRefOptions}
                    onChange={(v) => set("harnessId", v)}
                    placeholder="Select"
                    value={form.harnessId}
                    error={errs.harnessId}
                  />
                </div>
                {errs.harnessId && (
                  <p className="text-xs text-red-500 mt-1">{errs.harnessId}</p>
                )}
              </div>
              <div>
                <FLbl required={isSmileIntegrated()}>
                  Power Line Length (mm)
                </FLbl>
                <IconField icon="straighten">
                  <input
                    type="number"
                    className={`${iconInputCls}${errs.powerLineMm ? " border-2 border-red-500" : " border border-slate-200"}`}
                    placeholder="e.g. 1500"
                    value={form.powerLineMm}
                    onChange={(e) => set("powerLineMm", e.target.value)}
                  />
                </IconField>
                {errs.startDate && (
                  <p className="text-xs text-red-500 mt-1">{errs.startDate}</p>
                )}
              </div>
              <div>
                <FLbl required={isSmileIntegrated()}>Harness Length (mm)</FLbl>
                <IconField icon="straighten">
                  <input
                    type="number"
                    className={`${iconInputCls}${errs.harnessLenMm ? " border-2 border-red-500" : " border border-slate-200"}`}
                    placeholder="e.g. 2000"
                    value={form.harnessLenMm}
                    onChange={(e) => set("harnessLenMm", e.target.value)}
                  />
                  {errs.harnessLenMm && (
                    <p className="text-xs text-red-500 mt-1">
                      {errs.harnessLenMm}
                    </p>
                  )}
                </IconField>
              </div>
            </div>
          </Section>

          <Section icon="bolt" title="Power Parameters">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <FLbl required={isSmileIntegrated()}>Battery Voltage (V)</FLbl>
                <IconField icon="battery_charging_full">
                  <input
                    type="number"
                    step="0.1"
                    className={`${iconInputCls}${errs.battV ? " border-2 border-red-500" : " border border-slate-200"}`}
                    placeholder="e.g. 12.5"
                    value={form.battV}
                    onChange={(e) => set("battV", e.target.value)}
                  />
                </IconField>
                {errs.battV && (
                  <p className="text-xs text-red-500 mt-1">{errs.battV}</p>
                )}
              </div>
              <div>
                <FLbl required={isSmileIntegrated()}>Power Supply</FLbl>
                <div ref={(el) => (fieldRefs.current.powerSupply = el)}>
                  <AutoComplete
                    value={form.powerSupply}
                    onChange={(v) => set("powerSupply", v)}
                    options={powerSupplyOptions}
                    placeholder="Select"
                    error={errs.powerSupply}
                  />
                </div>
                {errs.powerSupply && (
                  <p className="text-xs text-red-500 mt-1">
                    {errs.powerSupply}
                  </p>
                )}
              </div>
              <div>
                <FLbl required={isSmileIntegrated()}>DUT Current (A)</FLbl>
                <IconField icon="electric_bolt">
                  <input
                    type="number"
                    step="0.1"
                    className={`${iconInputCls}${errs.dutCurrentA ? " border-2 border-red-500" : " border border-slate-200"}`}
                    placeholder="e.g. 5.0"
                    value={form.dutCurrentA}
                    onChange={(e) => set("dutCurrentA", e.target.value)}
                  />
                </IconField>
                {errs.dutCurrentA && (
                  <p className="text-xs text-red-500 mt-1">
                    {errs.dutCurrentA}
                  </p>
                )}
              </div>
            </div>
          </Section>
        </div>

        <div className="space-y-4 min-w-0">
          <Section icon="checklist" title="Test Scope">
            <FLbl required={isSmileIntegrated()}>Test Name</FLbl>
            <div ref={(el) => (fieldRefs.current.testNameIds = el)}>
              <MultiSelect
                icon="checklist"
                value={form.testNameIds}
                onChange={(v) => set("testNameIds", v)}
                options={testNameOptions}
                placeholder="Select test names…"
                error={errs.testNameIds}
              />
            </div>
            {errs.testNameIds && (
              <p className="text-xs text-red-500 mt-1">{errs.testNameIds}</p>
            )}
          </Section>

          <Section icon="memory" title="DUT References">
            <FLbl required={isSmileIntegrated()}>DUT Ref No</FLbl>
            <div ref={(el) => (fieldRefs.current.dutRefIds = el)}>
              <AutoComplete
                icon="memory"
                value={form.dutRefIds}
                onChange={(v) => set("dutRefIds", v)}
                options={dutRefOptions}
                placeholder="Type to search DUT ref…"
                multiple
                error={errs.dutRefIds}
              />
            </div>
            {errs.dutRefIds && (
              <p className="text-xs text-red-500 mt-1">{errs.dutRefIds}</p>
            )}
          </Section>

          <Section icon="description" title="Operating Mode">
            <FLbl required={isSmileIntegrated()}>Description</FLbl>
            <Textarea
              className={`${iCls} h-24 py-2.5 resize-none`}
              placeholder="Describe the operating mode…"
              value={form.opMode}
              onChange={(e) => set("opMode", e.target.value)}
              error={errs.opMode}
            />
          </Section>
        </div>
      </div>
      {hasPrivilege(PRIVILEGES.MP_EDIT) && (
        <FormActions
          onSave={handleSave}
          saving={saving}
          onPreview={() => setPreviewOpen(true)}
          onSubmit={onSubmitAll}
        />
      )}
    </>
  );
});

// ─────────────────────────────────────────────────────────────

export default TestInfoTab;
export { TestInfoTab };
